/**
 * WatchVideoLink.tsx — inline smooth-scroll anchor to the YouTube embed.
 *
 * Renders as an <a> with href="#targetId" for keyboard/AT fallback.
 * JS smooth-scrolls via scrollIntoView({ behavior: 'smooth', block: 'center' })
 * so the video centers in the viewport rather than top-aligning.
 * Respects prefers-reduced-motion: uses 'instant' scroll when active.
 *
 * CSS: .blog-watch-video-link in globals.css
 *   — color: --link-on-cream (#A38846)
 *   — weight: 600 (above body 400, signals navigational intent)
 *   — static half-opacity underline at rest → full on hover
 *   — CSS ::after appends '→' with 3px translate on hover
 *
 * Usage in MDX:
 *   <watchVideoLink targetId="video-NVccVdRL_oY">Watch the video</watchVideoLink>
 *
 * Props:
 *   targetId  — id attribute on the youtubeEmbed wrapper element. Required.
 *   children  — link text. Defaults to "Watch the video".
 *
 * Added 2026-05-09 per Aria component spec (video-blog migrations cycle 5).
 */

'use client';

interface WatchVideoLinkProps {
  targetId: string;
  children?: React.ReactNode;
  /** Optional plain-text label for aria-label. When provided, used directly.
   *  When absent, falls back to string-cast of children or 'Watch the video'. */
  ariaLabel?: string;
}

export default function WatchVideoLink({ targetId, children = 'Watch the video', ariaLabel }: WatchVideoLinkProps) {
  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    const el = document.getElementById(targetId);
    if (!el) return;
    const prefersReduced =
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    el.scrollIntoView({
      behavior: prefersReduced ? 'instant' : 'smooth',
      block: 'center',
    });
  }

  // P3 fix (cycle 5 fix-up 2026-05-09): when children is a React element
  // (TinaMarkdown AST wrapper), typeof children !== 'string'. The ariaLabel prop
  // is now provided by BlogPostPageClient.tsx using extractPlainTextFromTinaAst()
  // on the raw Tina AST before it's rendered. Falls back to the string cast.
  const computedLabel = ariaLabel ?? (typeof children === 'string' ? children : 'Watch the video');

  return (
    <a
      className="blog-watch-video-link"
      href={`#${targetId}`}
      onClick={handleClick}
      aria-label={`${computedLabel} — scroll to embedded video`}
    >
      {children}
    </a>
  );
}
